import { useEffect, useMemo, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { AlertTriangle, CheckCircle2, Info, PartyPopper } from "lucide-react";
import { SiteAlert } from "@/hooks/use-supabase-data";

interface SiteAlertModalProps {
  alert: SiteAlert | null;
  onDismiss: (alertId: string) => void;
}

export function SiteAlertModal({ alert, onDismiss }: SiteAlertModalProps) {
  const [open, setOpen] = useState(false);

  const memoizedAlert = useMemo(
    () =>
      alert
        ? {
            ...alert,
            variant: (alert.variant ?? "info") as SiteAlert["variant"],
          }
        : null,
    [
      alert?.id,
      alert?.title,
      alert?.message,
      alert?.variant,
      alert?.action_label,
      alert?.action_url,
    ],
  );

  useEffect(() => {
    if (memoizedAlert) {
      const storageKey = getStorageKey(memoizedAlert.id);
      const dismissed = typeof window !== "undefined" ? sessionStorage.getItem(storageKey) : null;
      if (!dismissed) {
        setOpen(true);
      }
    } else {
      setOpen(false);
    }
  }, [memoizedAlert]);

  if (!memoizedAlert) {
    return null;
  }

  const handleClose = () => {
    const storageKey = getStorageKey(memoizedAlert.id);
    if (typeof window !== "undefined") {
      sessionStorage.setItem(storageKey, "dismissed");
    }
    setOpen(false);
    onDismiss(memoizedAlert.id);
  };

  const { icon: IconComponent, accentClass, buttonClass } = getVariantMeta(
    memoizedAlert.variant ?? "info",
  );
  const showAction = memoizedAlert.action_label && memoizedAlert.action_url;

  const handleActionClick = () => {
    if (memoizedAlert.action_url) {
      window.open(memoizedAlert.action_url, "_blank", "noopener,noreferrer");
    }
    handleClose();
  };

  return (
    <Dialog open={open} onOpenChange={(value) => (value ? setOpen(true) : handleClose())}>
      <DialogContent className="w-[92vw] max-w-md rounded-2xl p-5 sm:p-6">
        <DialogHeader className="text-left">
          <div className="flex items-center gap-3">
            <div
              className={`flex h-10 w-10 items-center justify-center rounded-full ${accentClass}`}
            >
              <IconComponent className="h-5 w-5" />
            </div>
            <DialogTitle className="text-xl font-semibold">{memoizedAlert.title}</DialogTitle>
          </div>
          <DialogDescription className="pt-3 text-sm leading-relaxed text-foreground sm:text-base">
            {memoizedAlert.message}
          </DialogDescription>
        </DialogHeader>
        <div className="flex flex-col gap-3 pt-6 sm:flex-row sm:justify-end">
          {showAction && (
            <Button
              variant="outline"
              onClick={handleActionClick}
              className="order-2 sm:order-1 min-w-[120px] self-center sm:self-auto"
            >
              {memoizedAlert.action_label}
            </Button>
          )}
          <Button
            onClick={handleClose}
            className={`${buttonClass} min-w-[120px] self-center sm:self-auto px-6`}
          >
            Got it
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function getStorageKey(alertId: string) {
  return `site-alert-dismissed-${alertId}`;
}

function getVariantMeta(variant: SiteAlert["variant"]) {
  switch (variant) {
    case "success":
      return {
        icon: PartyPopper,
        accentClass: "bg-emerald-100 text-emerald-600",
        buttonClass:
          "bg-[linear-gradient(135deg,#FF7A18_0%,#FFB347_100%)] text-white shadow-sm hover:brightness-95 transition-all duration-200",
      };
    case "warning":
      return {
        icon: AlertTriangle,
        accentClass: "bg-amber-100 text-amber-600",
        buttonClass:
          "bg-[linear-gradient(135deg,#FF7A18_0%,#FFB347_100%)] text-white shadow-sm hover:brightness-95 transition-all duration-200",
      };
    case "danger":
      return {
        icon: AlertTriangle,
        accentClass: "bg-red-100 text-red-600",
        buttonClass:
          "bg-[linear-gradient(135deg,#FF7A18_0%,#FFB347_100%)] text-white shadow-sm hover:brightness-95 transition-all duration-200",
      };
    case "info":
    default:
      return {
        icon: Info,
        accentClass: "bg-blue-100 text-blue-600",
        buttonClass:
          "bg-[linear-gradient(135deg,#FF7A18_0%,#FFB347_100%)] text-white shadow-sm hover:brightness-95 transition-all duration-200",
      };
  }
}

